import { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { WorkforceStatus } from "@/types/workforce";
import { WorkforceSortField, WorkforceSortOrder } from "@/hooks/workforce/useWorkforcePaginated";

export type WorkforceViewMode = "card" | "list" | "kanban";

interface WorkforceFilterContextType {
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	statusFilter: WorkforceStatus[];
	setStatusFilter: (statuses: WorkforceStatus[]) => void;
	assignedToMe: boolean;
	setAssignedToMe: (assigned: boolean) => void;
	dateRangeStart: string;
	setDateRangeStart: (date: string) => void;
	dateRangeEnd: string;
	setDateRangeEnd: (date: string) => void;
	endingSoon: number | null; // Days threshold (7, 30, or null)
	setEndingSoon: (days: number | null) => void;
	sortField: WorkforceSortField;
	setSortField: (field: WorkforceSortField) => void;
	sortOrder: WorkforceSortOrder;
	setSortOrder: (order: WorkforceSortOrder) => void;
	viewMode: WorkforceViewMode;
	setViewMode: (mode: WorkforceViewMode) => void;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	clearAllFilters: () => void;
}

interface WorkforceFilterProviderProps {
	children: ReactNode;
	viewMode?: WorkforceViewMode;
	setViewMode?: (mode: WorkforceViewMode) => void;
}

const WorkforceFilterContext = createContext<WorkforceFilterContextType | undefined>(undefined);

export function WorkforceFilterProvider({
	children,
	viewMode: externalViewMode,
	setViewMode: externalSetViewMode
}: WorkforceFilterProviderProps) {
	const [searchParams, setSearchParams] = useSearchParams();

	// Initialize state from URL parameters
	const [searchTerm, setSearchTerm] = useState(() => searchParams.get("search") || "");
	const [statusFilter, setStatusFilter] = useState<WorkforceStatus[]>(() => {
		const status = searchParams.get("status");
		return status ? status.split(",") as WorkforceStatus[] : [];
	});
	const [assignedToMe, setAssignedToMe] = useState<boolean>(() => searchParams.get("assignedToMe") === "true");
	const [dateRangeStart, setDateRangeStart] = useState(() => searchParams.get("dateStart") || "");
	const [dateRangeEnd, setDateRangeEnd] = useState(() => searchParams.get("dateEnd") || "");
	const [endingSoon, setEndingSoon] = useState<number | null>(() => {
		const days = searchParams.get("endingSoon");
		return days ? parseInt(days, 10) : null;
	});
	const [sortField, setSortField] = useState<WorkforceSortField>(() => {
		const field = searchParams.get("sortField");
		return (field as WorkforceSortField) || "dateCreated";
	});
	const [sortOrder, setSortOrder] = useState<WorkforceSortOrder>(() => {
		const order = searchParams.get("sortOrder");
		return (order as WorkforceSortOrder) || "desc";
	});
	const [internalViewMode, setInternalViewMode] = useState<WorkforceViewMode>(() => {
		const mode = searchParams.get("view");
		return (mode as WorkforceViewMode) || "kanban";
	});
	const [currentPage, setCurrentPage] = useState(() => {
		const page = searchParams.get("page");
		return page ? parseInt(page, 10) : 1;
	});

	// Use either external view mode state or internal
	const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
	const setViewMode = externalSetViewMode || setInternalViewMode;

	// Track last URL params we set to avoid unnecessary updates
	const lastParamsRef = useRef<string>("");
	const isInitialMount = useRef(true);

	// Update URL parameters whenever filters change
	useEffect(() => {
		// Skip URL sync on initial mount (params are already in URL)
		if (isInitialMount.current) {
			isInitialMount.current = false;
			lastParamsRef.current = searchParams.toString();
			return;
		}

		const params = new URLSearchParams();

		if (searchTerm)
			params.set("search", searchTerm);
		if (statusFilter.length > 0)
			params.set("status", statusFilter.join(","));
		if (assignedToMe)
			params.set("assignedToMe", "true");
		if (dateRangeStart)
			params.set("dateStart", dateRangeStart);
		if (dateRangeEnd)
			params.set("dateEnd", dateRangeEnd);
		if (endingSoon !== null)
			params.set("endingSoon", endingSoon.toString());
		if (sortField !== "dateCreated")
			params.set("sortField", sortField);
		if (sortOrder !== "desc")
			params.set("sortOrder", sortOrder);
		if (viewMode !== "kanban")
			params.set("view", viewMode);
		if (currentPage !== 1)
			params.set("page", currentPage.toString());

		// Only update URL if params are different from last time we set them
		const newParamsString = params.toString();
		if (newParamsString !== lastParamsRef.current) {
			lastParamsRef.current = newParamsString;
			setSearchParams(params, { replace: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchTerm, statusFilter, assignedToMe, dateRangeStart, dateRangeEnd, endingSoon, sortField, sortOrder, viewMode, currentPage]);

	const clearAllFilters = () => {
		setSearchTerm("");
		setStatusFilter([]);
		setAssignedToMe(false);
		setDateRangeStart("");
		setDateRangeEnd("");
		setEndingSoon(null);
		setCurrentPage(1);
	};

	return (
		<WorkforceFilterContext.Provider
			value={{
				searchTerm,
				setSearchTerm,
				statusFilter,
				setStatusFilter,
				assignedToMe,
				setAssignedToMe,
				dateRangeStart,
				setDateRangeStart,
				dateRangeEnd,
				setDateRangeEnd,
				endingSoon,
				setEndingSoon,
				sortField,
				setSortField,
				sortOrder,
				setSortOrder,
				viewMode,
				setViewMode,
				currentPage,
				setCurrentPage,
				clearAllFilters,
			}}
		>
			{children}
		</WorkforceFilterContext.Provider>
	);
}

export const useWorkforceFilter = () => {
	const context = useContext(WorkforceFilterContext);
	if (context === undefined) {
		throw new Error("useWorkforceFilter must be used within a WorkforceFilterProvider");
	}
	return context;
};
